import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Routes,
  Route,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import './App.css';
import Layout from './components/layout/Layout';
import PageNotFound from './pages/PageNotFound';
import { get_user_data } from './redux/Slice/userSlice';
import store from './redux/store';
import Library from './pages/Dashboard/Library/Library';
import LibraryLayout from './components/layout/LibraryLayout';
import Dashboard from './pages/Dashboard/HomePage';
import MyProject from './pages/Dashboard/MyProject';
import GridPage from './pages/Dashboard/GridPage';
import Teams from './pages/Dashboard/Teams';
import Tutorial from './pages/Dashboard/Tutorial';
import TutorialLayout from './components/layout/TeamsLayout';
import Analytics from './pages/Dashboard/Analytics';
import Notifications from './pages/Dashboard/Notification';
import NotificationDetails from './pages/Dashboard/NotificationMobile';
import toast from 'react-hot-toast';
import ProfileLayout from './components/layout/ProfileLayout';
import Profile from './pages/Profile/Profile';
import { useDispatch, useSelector } from 'react-redux';
import UpdateData from './pages/Update';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './components/LoginPage';
import useSessionTimeout from './Hooks/useSessionTimeout';
import Loader from './components/Loader';
import PreviewPage from './pages/Dashboard/previewPage';
import { redo, undo } from './components/gridpagefunction/UndoReedo';
import * as Three from 'three';
import VRAssetMindMap from './components/VRComponent/VrAssetMindMap';
import TrainingUpload from './pages/Training/TrainingUpload';
import GLBAnimationViewer from './components/SampleViewer';

// Scroll to top on route change
const ScrollTop = ({ children }) => {
  const location = useLocation();
  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [location.pathname]);
  return children;
};

const App = () => {
  const location = useLocation();
  const { pathname } = location;
  let user_data = JSON.parse(localStorage.getItem('user'));
  const isGridPage = location.pathname.startsWith('/gridpage');
  const viewHelper = document.getElementById('viewHelper');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Save the current path to sessionStorage whenever it changes
  const sceneRef = useRef(new Three.Scene());

  useEffect(() => {
    if (pathname !== '/' && user_data) {
      sessionStorage.setItem('lastPath', pathname);
    }
  }, [pathname, user_data]);
  // useMediaPause();
  // Handle view helper visibility
  useEffect(() => {
    if (viewHelper) {
      viewHelper.style.display = isGridPage ? 'block' : 'none';
    }
  }, [isGridPage, viewHelper]);

  // Initial navigation after user login
  useEffect(() => {
    if (user_data) {
      // Don't navigate to dashboard if we're already on a specific page
      const lastPath = sessionStorage.getItem('lastPath');

      // Only redirect to dashboard if:
      // 1. We're at the root path (/)
      // 2. We're not on a grid page
      // 3. We don't have a saved path
      if (pathname === '/' && !isGridPage && !lastPath) {
        navigate('/dashboard');
      }
      // If we have a saved path and we're at the root, navigate to the saved path
      else if (pathname === '/' && lastPath) {
        navigate(lastPath);
      }

      // Dispatch user data in any case
      store.dispatch(get_user_data(user_data));
    }
  }, [user_data, pathname, navigate, isGridPage]);

  // Handle URL parameters (for login redirects)
  useLayoutEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.size > 0) {
      const userData = params.get('userData')
        ? JSON.parse(decodeURIComponent(params.get('userData')))
        : null;
      const message = params.get('message')
        ? decodeURIComponent(params.get('message'))
        : null;
      console.log(userData, 'userData');
      if (userData) {
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', userData.accessToken);
        dispatch(get_user_data(userData)); // Store user data in Redux

        // Check if there's a saved path to return to
        if (userData) {
          navigate('/dashboard'); // Navigate to saved path or dashboard
          window.location.reload();
        }
        console.log(userData, 'userData');
      } else if (message) {
        alert(message); // Show message from Google auth (e.g. "User not found. Please register first.")
        window.history.replaceState({}, '', window.location.pathname); // Clear ?message= from URL
      }
    }
  }, [dispatch, navigate]);

  // Session Timeout Code Start
  const handleSessionExpire = () => {
    // Save the current path before clearing localStorage
    if (pathname !== '/') {
      sessionStorage.setItem('lastPath', pathname);
    }

    localStorage.clear();
    navigate('/');
  };

  const { sessionExpire } = useSessionTimeout(
    user_data != null,
    3600,
    handleSessionExpire
  );

  useEffect(() => {
    if (sessionExpire) {
      toast.error('Your session has expired. Please log in again.', {
        id: 'session-expired',
      });
    } else {
      toast.dismiss('session-expired');
    }
  }, [sessionExpire]);

  // Session Timeout Code end
  const [loadingNew, setLoadingNew] = useState(true);

  useEffect(() => {
    setLoadingNew(true);
    if (
      pathname.startsWith('/gridpage/') ||
      pathname.startsWith('/preview-page/')
    ) {
      setTimeout(() => {
        setLoadingNew(false);
      }, 200);
    } else {
      // For other routes, set loading to false immediately
      setLoadingNew(false);
    }
  }, [pathname]);
  const { loading, loadingMessage } = useSelector(
    (state) => state.global_state
  );

  useEffect(() => {
    const stopPropagationOnInputs = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) {
        e.stopPropagation();
      }
    };

    // Stop keydown events from bubbling up to the window when typing in input components.
    // This prevents global shortcuts (like delete, copy, paste, etc.) from triggering.
    document.addEventListener('keydown', stopPropagationOnInputs);

    return () => {
      document.removeEventListener('keydown', stopPropagationOnInputs);
    };
  }, []);

  return (
    <>
      <ScrollTop />

      <Routes>
        <Route path="/update" element={<UpdateData />} />
        <Route element={<MainLayout />}>
          {user_data && (
            <>
              <Route element={<Layout />}>
                <Route path="/dashboard" element={<Outlet />}>
                  <Route index element={<Dashboard />} />
                  <Route path="myprojects" element={<MyProject />} />
                  <Route
                    path="sample-viewer"
                    element={<GLBAnimationViewer />}
                  />

                  <Route path="library" element={<LibraryLayout />}>
                    <Route path=":category" element={<Library />} />
                  </Route>
                  <Route path="teams" element={<Teams />} />
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="notifications" element={<Notifications />} />
                  <Route
                    path="notification-details"
                    element={<NotificationDetails />}
                  />
                  <Route path="tutorials" element={<TutorialLayout />}>
                    <Route path=":category" element={<Tutorial />} />
                  </Route>
                  <Route path="training" element={<TrainingUpload />} />
                  <Route path="*" element={<PageNotFound />} />
                </Route>
              </Route>
              <Route element={<ProfileLayout />}>
                <Route path="/profile" element={<Profile />} />
              </Route>
            </>
          )}
        </Route>
        <Route
          path="/gridpage/:id"
          element={
            <>
              {/* {loading && <Loader />} */}
              {(loadingNew || loading || loadingMessage) && (
                <Loader
                  bgColor={loadingMessage ? 'bg-[rgba(0,0,0,0.5)]' : ''}
                />
              )}
              <GridPage />
            </>
          }
        />
        <Route
          path="/preview-page/:id"
          element={
            <>
              {(loadingNew || loading || loadingMessage) && (
                <Loader
                  bgColor={loadingMessage ? 'bg-[rgba(0,0,0,0.5)]' : ''}
                />
              )}
              <PreviewPage />
            </>
          }
        />
        <Route path="*" element={<PageNotFound bg={'black'} />} />
        <Route path="vr-mindmap" element={<VRAssetMindMap />} />
        <Route path="vr-mindmap/:id" element={<VRAssetMindMap />} />
        {!user_data && (
          <Route
            path="/"
            element={
              <div className="w-screen h-screen flex items-center justify-center">
                <LoginPage
                  closeModal={() => {
                    window.location.href =
                      process.env.REACT_APP_SPATIAL_WEB_URL;
                  }}
                />
              </div>
            }
          />
        )}
        {user_data && <Route path="/" element={<></>} />}
      </Routes>
    </>
  );
};

export default App;
